import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SubmissionContext } from '../utils/SubmissionContextProvider';
import MediaSelector from './MediaSelector';
import AcceptTerms from './AcceptTerms';

const StepViewPropTypes = {
  currentStep: PropTypes.bool
};

const useInitialFocus = (isCurrent, ref) => {
  useEffect(() => {
    // Only use auto-focusing on desktops and for empty inputs
    if (
      isCurrent &&
      ref?.current?.value.trim() === '' &&
      !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    ) {
      setTimeout(() => {
        ref.current.focus();
      }, 200);
    }
  }, [ref, isCurrent]);
};

const TextBoxStep = ({ currentStep, placeholder, value, setValue, type = 'text' }) => {
  const inputRef = useRef(null);
  useInitialFocus(currentStep, inputRef);
  const commonProps = {
    placeholder,
    value,
    onChange: e => setValue(e.target.value),
    ref: inputRef,
    disabled: !currentStep
  };
  return type === 'textarea' ? (
    <textarea type='text' {...commonProps} />
  ) : (
    <input type={type} {...commonProps} />
  );
};
TextBoxStep.propTypes = {
  currentStep: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  setValue: PropTypes.func,
  type: PropTypes.oneOf(['text', 'email', 'textarea'])
};

export const StepTitle = ({ currentStep }) => {
  const { title, setTitle } = useContext(SubmissionContext);
  return <TextBoxStep value={title} setValue={setTitle} {...{ currentStep }} />;
};
StepTitle.propTypes = StepViewPropTypes;

export const StepDescription = ({ currentStep }) => {
  const { description, setDescription } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder='Erzähle uns, was Du vorhast.'
      value={description}
      setValue={setDescription}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepDescription.propTypes = StepViewPropTypes;

export const StepWhySolutionNeeded = ({ currentStep }) => {
  const { whySolutionNeeded, setWhySolutionNeeded } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder=''
      value={whySolutionNeeded}
      setValue={setWhySolutionNeeded}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepWhySolutionNeeded.propTypes = StepViewPropTypes;

export const StepHowSolutionSustainable = ({ currentStep }) => {
  const { howSolutionSustainable, setHowSolutionSustainable } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder=''
      value={howSolutionSustainable}
      setValue={setHowSolutionSustainable}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepHowSolutionSustainable.propTypes = StepViewPropTypes;

export const StepWhichRegion = ({ currentStep }) => {
  const { whichRegion, setWhichRegion } = useContext(SubmissionContext);
  return <TextBoxStep value={whichRegion} setValue={setWhichRegion} {...{ currentStep }} />;
};
StepWhichRegion.propTypes = StepViewPropTypes;

export const StepWhyPromoteYourRegion = ({ currentStep }) => {
  const { whyPromoteYourRegion, setWhyPromoteYourRegion } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder='Erzähle uns z. B., wie viele Menschen in Deiner Region von der Umsetzung Deiner Idee profitieren würden.'
      value={whyPromoteYourRegion}
      setValue={setWhyPromoteYourRegion}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepWhyPromoteYourRegion.propTypes = StepViewPropTypes;

export const StepUsePrizeMoney = ({ currentStep }) => {
  const { usePrizeMoney, setUsePrizeMoney } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder='Beschreibe Deinen Plan zur Umsetzung der Idee. Wie würde das Preisgeld dabei helfen?'
      value={usePrizeMoney}
      setValue={setUsePrizeMoney}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepUsePrizeMoney.propTypes = StepViewPropTypes;

export const StepMedia = () => {
  const { media, setMedia } = useContext(SubmissionContext);
  return <MediaSelector {...{ media, setMedia }} />;
};
StepMedia.propTypes = StepViewPropTypes;

export const StepFirstname = ({ currentStep }) => {
  const { firstname, setFirstname } = useContext(SubmissionContext);
  return (
    <TextBoxStep placeholder='' value={firstname} setValue={setFirstname} {...{ currentStep }} />
  );
};
StepFirstname.propTypes = StepViewPropTypes;

export const StepLastname = ({ currentStep }) => {
  const { lastname, setLastname } = useContext(SubmissionContext);
  return (
    <TextBoxStep placeholder='' value={lastname} setValue={setLastname} {...{ currentStep }} />
  );
};
StepLastname.propTypes = StepViewPropTypes;

export const StepAboutYourself = ({ currentStep }) => {
  const { aboutYourself, setAboutYourself } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder=''
      value={aboutYourself}
      setValue={setAboutYourself}
      type='textarea'
      {...{ currentStep }}
    />
  );
};
StepAboutYourself.propTypes = StepViewPropTypes;

export const StepDateOfBirth = ({ currentStep }) => {
  const { dateOfBirth, setDateOfBirth } = useContext(SubmissionContext);

  const manipulateValue = val => {
    return val.length > dateOfBirth.length && (/^\d\d$/.test(val) || /^\d\d.\d\d$/.test(val))
      ? `${val}.`
      : val;
  };
  return (
    <TextBoxStep
      placeholder='TT.MM.JJJJ'
      value={dateOfBirth}
      setValue={val => setDateOfBirth(manipulateValue(val))}
      {...{ currentStep }}
    />
  );
};
StepDateOfBirth.propTypes = StepViewPropTypes;

export const StepEmail = ({ currentStep }) => {
  const { email, setEmail } = useContext(SubmissionContext);
  return <TextBoxStep type='email' value={email} setValue={setEmail} {...{ currentStep }} />;
};
StepEmail.propTypes = StepViewPropTypes;

export const StepRegionOrPlace = ({ currentStep }) => {
  const { regionOrPlace, setRegionOrPlace } = useContext(SubmissionContext);
  return (
    <TextBoxStep
      placeholder='Region oder Ort'
      value={regionOrPlace}
      setValue={setRegionOrPlace}
      {...{ currentStep }}
    />
  );
};
StepRegionOrPlace.propTypes = StepViewPropTypes;

export const StepSubmit = () => {
  const { acceptTerms, setAcceptTerms } = useContext(SubmissionContext);
  return (
    <>
      <p style={{ marginTop: '-30px' }}>
        Vor dem Absenden musst Du noch den Teilnahmebedingungen zustimmen.
      </p>
      <AcceptTerms accepted={acceptTerms} setAccepted={setAcceptTerms} />
      <p>
        <b style={{ fontSize: '1rem', lineHeight: '1rem' }}>Alle Fragen beantwortet?</b> Je mehr wir
        über Deine Idee wissen, desto höher sind Deine Chancen auf das Preisgeld. Falls wichtige
        Angaben fehlen, können wir Deine Idee nicht berücksichtigen.
      </p>
    </>
  );
};
